/**
 * network/models.ts — types for Module #8, the Roamer Network™.
 *
 * A platform MODULE, not a core engine. Location-aware professional
 * networking for verified WorkRoamers — profile, peer discovery,
 * connections, meetups, collaboration, local groups, verification
 * indicators, reputation, and privacy. NOT a social media platform.
 * Honesty rules as everywhere: unknown ⇒ null, peers/reputation exist only
 * as recorded data (a future backend syncs real peers — nothing is
 * fabricated locally), verification is future-scope with evidence-based
 * indicators, and EVERYTHING private is opt-in, off by default.
 */

// ─── Profile ───────────────────────────────────────────────────────────────────

export type NetworkAvailability = 'openToMeet' | 'busy' | 'headsDown' | 'unknown';
export type WorkStyle = 'async' | 'coworking' | 'cafe' | 'mixed' | null;
export type VerificationStatus = 'unverified' | 'pending' | 'verified';

export interface NetworkProfile {
  displayName: string | null;
  professionalTitle: string | null;
  /** Fed by LocationChanged; exposure gated by shareCurrentBase. */
  currentBase: string | null;
  homeCountry: string | null;
  languages: string[];
  skills: string[];
  industries: string[];
  portfolioLinks: string[];
  remoteExperienceYears: number | null;
  /** Recorded count — never guessed. */
  countriesVisited: number | null;
  /** Comes from recorded platform data (e.g. a future Passport sync); null until then. */
  roamerLevel: string | null;
  /** Mirrored from achievementUnlocked bus notifications — titles only. */
  achievements: string[];
  availability: NetworkAvailability;
  interests: string[];
  workStyle: WorkStyle;
  /** Verification is FUTURE scope — stays 'unverified' until a real process runs. */
  verificationStatus: VerificationStatus;
}

export const DEFAULT_NETWORK_PROFILE: NetworkProfile = {
  displayName: null,
  professionalTitle: null,
  currentBase: null,
  homeCountry: null,
  languages: [],
  skills: [],
  industries: [],
  portfolioLinks: [],
  remoteExperienceYears: null,
  countriesVisited: null,
  roamerLevel: null,
  achievements: [],
  availability: 'unknown',
  interests: [],
  workStyle: null,
  verificationStatus: 'unverified',
};

// ─── Peers & discovery ─────────────────────────────────────────────────────────

/**
 * A peer record — recorded locally or synced by a FUTURE backend provider.
 * The module never invents peers; an empty directory yields empty discovery.
 */
export interface PeerProfile {
  id: string;
  displayName: string;
  professionalTitle: string | null;
  city: string | null;
  country: string | null;
  /** Coordinates enable the distance filter — both sides must have them. */
  latitude: number | null;
  longitude: number | null;
  languages: string[];
  skills: string[];
  industries: string[];
  interests: string[];
  remoteExperienceYears: number | null;
  availability: NetworkAvailability;
  verified: boolean;
  addedAt: number;
}

export interface DiscoveryFilters {
  /** Requires recorded coordinates on the user's own profile AND the peer. */
  maxDistanceKm?: number;
  profession?: string;
  industry?: string;
  language?: string;
  minExperienceYears?: number;
  city?: string;
  country?: string;
  skill?: string;
  availability?: NetworkAvailability;
  verifiedOnly?: boolean;
  /** Peers sharing ≥1 interest with the user's own recorded interests. */
  sharedInterestsOnly?: boolean;
}

// ─── Connections ───────────────────────────────────────────────────────────────

export type ConnectionRequestStatus = 'pending' | 'accepted' | 'declined';

export interface ConnectionRequest {
  id: string;
  peerId: string;
  direction: 'incoming' | 'outgoing';
  message: string;
  status: ConnectionRequestStatus;
  createdAt: number;
  resolvedAt: number | null;
}

export interface NetworkConnection {
  peerId: string;
  favorite: boolean;
  /** Follower relationships are optional and one-way. */
  follower: boolean;
  notes: string;
  connectedAt: number;
}

/** Evidence-based suggestion — never a cold guess. */
export interface ConnectionSuggestion {
  peerId: string;
  displayName: string;
  /** Recorded overlaps that justify the suggestion. Never empty. */
  basis: string[];
}

// ─── Meetups ───────────────────────────────────────────────────────────────────

export const MEETUP_KINDS = [
  'coffee',
  'coworkingSession',
  'lunch',
  'networkingEvent',
  'skillSharing',
  'languageExchange',
  'weekendTrip',
  'photographyWalk',
  'startupMeetup',
] as const;
export type MeetupKind = (typeof MEETUP_KINDS)[number];

export interface Meetup {
  id: string;
  kind: MeetupKind;
  title: string;
  city: string;
  venue: string | null;
  scheduledFor: number | null;
  /** Peer ids who accepted; the organizer is implicit (the user). */
  attendeePeerIds: string[];
  attended: boolean;
  createdAt: number;
}

// ─── Collaboration ─────────────────────────────────────────────────────────────

export const COLLABORATION_KINDS = [
  'projectCollaboration',
  'freelanceTeam',
  'accountabilityPartner',
  'studyGroup',
  'mastermindGroup',
  'businessPartnership',
] as const;
export type CollaborationKind = (typeof COLLABORATION_KINDS)[number];

export interface Collaboration {
  id: string;
  kind: CollaborationKind;
  title: string;
  peerIds: string[];
  status: 'proposed' | 'active' | 'completed' | 'ended';
  notes: string;
  createdAt: number;
}

// ─── Local groups ──────────────────────────────────────────────────────────────

export interface LocalGroup {
  id: string;
  name: string; // e.g. "Bangkok Remote Workers"
  city: string;
  interests: string[];
  /** Recorded count only — null when unknown, never a fabricated number. */
  memberCount: number | null;
  joined: boolean;
  joinedAt: number | null;
  addedAt: number;
}

// ─── Verification (FUTURE — evidence-based indicators only) ────────────────────

export const VERIFICATION_INDICATOR_KINDS = [
  'verifiedIdentity',
  'verifiedProfessional',
  'missionHistory',
  'communityReputation',
  'passportProgress',
  'contributionQuality',
] as const;
export type VerificationIndicatorKind = (typeof VERIFICATION_INDICATOR_KINDS)[number];

export interface VerificationIndicator {
  kind: VerificationIndicatorKind;
  /** Recorded evidence backing the indicator. Required. */
  evidence: string;
  recordedAt: number;
}

// ─── Reputation ────────────────────────────────────────────────────────────────

export interface Recommendation {
  id: string;
  fromPeerId: string;
  text: string;
  recordedAt: number;
}

export interface CommunityReputation {
  helpfulReports: number;
  meetupsAttended: number;
  contributions: number;
  /** From MissionCompleted bus events. */
  missionParticipation: number;
  recommendations: number;
  /**
   * 0–100, computed only when ≥3 recorded reputation signals exist;
   * null otherwise — no fabricated trust.
   */
  communityTrust: number | null;
}

// ─── Privacy (EVERYTHING opt-in, off by default) ───────────────────────────────

export interface NetworkPrivacySettings {
  /** Master switch — off ⇒ nothing is shared/exported and no bus publications. */
  visible: boolean;
  shareCurrentBase: boolean;
  shareOnlineStatus: boolean;
  shareProfessionalDetails: boolean;
  allowConnectionRequests: boolean;
  meetupAvailability: boolean;
  /** Direct messages are FUTURE scope; the toggle exists, the feature does not. */
  allowDirectMessages: boolean;
}

export const DEFAULT_NETWORK_PRIVACY: NetworkPrivacySettings = {
  visible: false,
  shareCurrentBase: false,
  shareOnlineStatus: false,
  shareProfessionalDetails: false,
  allowConnectionRequests: false,
  meetupAvailability: false,
  allowDirectMessages: false,
};

// ─── Export ────────────────────────────────────────────────────────────────────

export interface NetworkExport {
  exportedAt: number;
  /** null unless visible; currentBase / professional details stripped per toggles. */
  profile: NetworkProfile | null;
  peers: PeerProfile[];
  connections: NetworkConnection[];
  requests: ConnectionRequest[];
  meetups: Meetup[];
  collaborations: Collaboration[];
  groups: LocalGroup[];
  indicators: VerificationIndicator[];
  recommendations: Recommendation[];
}
